import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useLauncherStore } from "../../../../lib/client/contexts/LauncherStoreContext";
import type { MessengerConfig } from "@deskpro-product/messenger-api-schemas/messenger/config";
import { useLauncherContext } from "../../../../lib/client/contexts/LauncherContext";

type BusinessHourRange = {
  start: string; // ISO date
  end: string; // ISO date
};

type BusinessHoursConfig = {
  periodStart: string; // ISO date
  periodEnd: string; // ISO date
  businessHours: {
    [setId: string]: BusinessHourRange[];
  };
};

type BusinessHoursContextType = {
  isWithinBusinessHours: (setId: string) => boolean;
  ready: boolean;
};

const BusinessHoursContext = createContext<BusinessHoursContextType | undefined>(undefined);

export function BusinessHoursProvider({ children }: { children: ReactNode }) {
  const { api } = useLauncherContext();
  const [configs, setConfigs] = useState<BusinessHoursConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [initRan, setInitRan] = useState(false);
  const config = useLauncherStore((state) => state.config);

  useEffect(() => {
    async function fetchBusinessHours() {
      setInitRan(true);
      const setIds = extractBusinessHourSetIds(config);

      if (setIds.length > 0) {
        setLoading(true);
        try {
          const businessHours = await api.getBusinessHours(setIds);
          setConfigs(businessHours);
        } finally {
          setLoading(false);
        }
      }
    }

    fetchBusinessHours();
  }, [api, config]);

  const extractBusinessHourSetIds = (config: MessengerConfig | undefined) => {
    if (!config?.routes) return [];

    return config.routes.flatMap(
      ({ cards }) =>
        cards
          ?.map(({ card }) =>
            card.type === "FlowLinkCard" &&
            card.businessHoursEnabled &&
            card.businessHoursSettings?.setId
              ? card.businessHoursSettings.setId
              : null,
          )
          .filter((id): id is string => Boolean(id)) ?? [],
    );
  };

  const isWithinBusinessHours = (setId: string) => {
    if (!configs) return true; // Default to showing if config not loaded
    const businessHourSet = configs.businessHours[setId];
    if (!businessHourSet) return true; // Default to showing if set not found
    const now = new Date();
    // Check if current time is within any of the business hours ranges
    return businessHourSet.some(({ start, end }) => {
      try {
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          return false;
        }
        return now >= startDate && now <= endDate;
      } catch (e) {
        return false;
      }
    });
  };

  const ready = !loading && initRan;
  return (
    <BusinessHoursContext.Provider value={{ isWithinBusinessHours, ready }}>
      {children}
    </BusinessHoursContext.Provider>
  );
}

export function useBusinessHoursContext() {
  const context = useContext(BusinessHoursContext);
  if (!context) {
    throw new Error("useBusinessHoursContext must be used within a BusinessHoursProvider");
  }
  return context;
}
